import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Screen, PrimaryButton } from '../ui/Screen.jsx'
import SafetyScreen from './SafetyScreen.jsx'
import { resolveCrisis } from '../safety.js'
import { useConfigBundle } from '../config.js'

/**
 * The support screen, reachable at any time by anyone, triggered by nothing.
 *
 * DS-5 §5.1: if support is reachable from every screen at any moment, for
 * everyone, regardless of any answer, then an interposed safety screen stops
 * being "the app revealing resources" and becomes "the app making sure you saw
 * them". The resources were always there. That is a much smaller claim about
 * the person, and it is the single strongest mitigation available for the
 * feedback problem — which is why DS-1 part 2 called rec B a PRECONDITION for
 * the interposition rules rather than a companion to them.
 *
 * IT RENDERS LEVEL 1, and that is not a placeholder. There is no level here
 * because nothing was triggered, and level 1 is config's own gentlest copy —
 * "Support is available", or "Where to find support" where nothing is
 * displayable. Inventing a fourth, non-triggered copy set would put a
 * participant-facing string in this file, which is the one thing this app never
 * does. Same resource state resolution as every other path, server-owned.
 *
 * NO DISMISSAL, because nothing is being dismissed. SafetyScreen's control is
 * suppressed and this screen carries an ordinary Back instead: a dismissal
 * control here would imply she had been sent somewhere, which is exactly the
 * reading the permanent affordance exists to prevent.
 *
 * This is also the screen DS-5 §5.2 shows once during onboarding, unprompted,
 * to everyone — so its meaning is established before it can ever be read as a
 * verdict on an answer.
 */
export default function Support() {
  const navigate = useNavigate()
  /*
   * DS-5 §5.2 — THE ONE-TIME SIGHTING DURING ONBOARDING.
   *
   * If the first time she meets this screen is when her own answer triggered
   * it, its meaning is fixed at that moment as "a response to me". Shown once
   * here, calmly and in the flow, it means "here is what exists" before any
   * answer can make it a verdict. That removes novelty entirely and is the
   * honest demonstration of a promise consent has already made — consent tells
   * her the app can show support lines for her country, and until now nothing
   * ever did.
   *
   * The CONTENT is identical either way, deliberately: a special onboarding
   * variant would defeat the point, which is that she has already seen exactly
   * this screen. Only the way onward differs.
   */
  const [params] = useSearchParams()
  const onboarding = params.get('onboarding') === '1'
  const { data: bundle } = useConfigBundle()
  const [content, setContent] = useState(null)
  const [resolved, setResolved] = useState(false)

  useEffect(() => {
    let live = true
    // Level 1: the gentlest copy config carries, and the only honest choice for
    // a screen nothing triggered.
    resolveCrisis(1, bundle)
      .then((next) => {
        if (!live) return
        setContent(next)
        setResolved(true)
      })
      .catch(() => {
        if (live) setResolved(true)
      })
    return () => {
      live = false
    }
  }, [bundle])

  if (!resolved) {
    // §3.10: no spinner. Nothing, briefly.
    return <Screen>{null}</Screen>
  }

  return (
    <SafetyScreen
      level={content?.level}
      resources={content?.resources}
      countryName={content?.countryName}
      // Nothing was triggered, so there is nothing to acknowledge and no
      // displaced control to earn.
      dismissalRequired={false}
      onDismiss={null}
      /*
       * §4.3 — the sole forward action of a screen is a PRIMARY, and it lives
       * in the band. F4 named this exact screen: its only way onward rendered
       * as an outline button.
       */
      action={
        onboarding ? (
          <PrimaryButton onClick={() => navigate('/demographics', { replace: true })}>
            Continue
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={() => navigate(-1)}>Close</PrimaryButton>
        )
      }
    />
  )
}
